// src/paths/PathChatPanel.tsx — path chat, the ONLY way to modify a path
// graph (v1-spec step 4). Collapsible right panel on /paths/:id (Francis
// 2026-09-01): the graph stays read-only; "split step 4", "skip what I
// know", "mark done — I did it elsewhere", "give me another way in" all go
// through here. Each applied turn returns the fresh full path and the graph
// repaints via onPathUpdate.
import React, { useEffect, useRef, useState } from 'react';
import { pathsApi } from '../services/api';
import { PathChatMessage, PathResponse } from '../types/paths';

const GREETING: PathChatMessage = {
  role: 'assistant',
  content:
    'This is your path’s control room — I can reshape the map for you. ' +
    'Try “skip step 2, I know it”, “mark step 3 done — I did it elsewhere”, ' +
    'or “give me another way into a step you’re stuck on”.',
};

interface Props {
  pathId: string;
  // Canned message prefill (Try another way / Mark as done); nonce forces
  // re-seeding even when the same text is sent twice.
  seed?: { text: string; nonce: number } | null;
  onClose: () => void;
  onPathUpdate: (path: PathResponse) => void;
}

const PathChatPanel: React.FC<Props> = ({ pathId, seed, onClose, onPathUpdate }) => {
  const [messages, setMessages] = useState<PathChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Persisted history (the graph's audit trail) — loaded once per path.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await pathsApi.getPathChat(pathId);
        if (!cancelled) setMessages(res.messages);
      } catch (err) {
        console.error('[PATHS-CHAT] history load failed:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [pathId]);

  useEffect(() => {
    if (seed) {
      setInput(seed.text);
      inputRef.current?.focus();
    }
  }, [seed]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy, loaded]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setError(null);
    const next: PathChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await pathsApi.pathChat(pathId, trimmed);
      setMessages([
        ...next,
        {
          role: 'assistant',
          content: res.reply,
          ops: res.applied_ops > 0 ? new Array(res.applied_ops).fill({}) : [],
        },
      ]);
      onPathUpdate(res.path);
    } catch (err: any) {
      console.error('[PATHS-CHAT] turn failed:', err);
      setError(err?.response?.data?.detail || 'Could not process that — please try again.');
      // Roll the user message back into the input so nothing is lost.
      setMessages(messages);
      setInput(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const shown = loaded && messages.length === 0 ? [GREETING] : messages;

  return (
    <aside className="drawer pg-chat">
      <button className="dx" onClick={onClose} title="Close chat">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
      <div className="d-title">Path chat</div>
      <div className="pc-sub">
        The only way to reshape this path — split, skip, branch, or mark steps done.
      </div>

      <div className="pc-msgs" ref={scrollRef}>
        {!loaded && <div className="pc-loading">Loading conversation…</div>}
        {shown.map((m, i) => (
          <div key={i} className={`pc-row ${m.role}`}>
            <div className={`pc-bubble ${m.role}`}>
              {m.content}
              {m.role === 'assistant' && (m.ops?.length ?? 0) > 0 && (
                <div className="pc-updated">✦ path updated</div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="pc-row assistant">
            <div className="pc-bubble assistant pc-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="pc-error">{error}</div>}

      <form
        className="pc-input"
        onSubmit={(e) => { e.preventDefault(); send(input); }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask for a change…"
          maxLength={2000}
          disabled={busy}
        />
        <button type="submit" className="btn amber sm" disabled={!input.trim() || busy}>
          ➤
        </button>
      </form>
    </aside>
  );
};

export default PathChatPanel;
